if (!customElements.get('product-compare')) {
  const STORAGE_KEY = 'ep-compare-items-v3';
  const COMPARE_CHANGE_EVENT = 'compare:change';

  /* ── EP START: comparison rules engine v3 ──
     Identity first: each racket is classified as control, all-round or power from
     its shape, nudged by balance and weight. Comparisons are expressed relative to
     those identities, with magnitude tiers so small spec gaps produce tempered
     language instead of a false power-vs-control verdict. */
  const EP_COMPARE_WORKER_URL = 'https://ep-compare-workerjs.ewan-a80.workers.dev';

  const EP_COPY = {
    bothPrefix: 'Both: ',
    loadingMessages: [
      'Reading both product descriptions',
      'Comparing construction and feel',
      'Weighing up the value',
      'Writing your comparison'
    ],
    shapeTraits: {
      round: { base: 1, gives: 'more control and a bigger, more forgiving sweet spot' },
      teardrop: { base: 2, gives: 'a balance of control and power' },
      hybrid: { base: 2.5, gives: 'a blend of power and control tilted toward attack' },
      diamond: { base: 3, gives: 'maximum power for attacking play' }
    },
    identityLabels: { control: 'control', allround: 'all-round', power: 'power' }
  };

  const EP_COLOUR_WORDS = ['blue', 'red', 'black', 'white', 'green', 'yellow', 'purple', 'orange', 'pink', 'grey', 'gray', 'silver', 'gold', 'navy', 'teal', 'mint', 'coral', 'lime', 'turquoise'];

  const EPCompareEngine = {
    norm(value) {
      return (value === null || value === undefined) ? '' : String(value).toLowerCase().trim();
    },

    shortName(title) {
      return String(title || '')
        .replace(/\s*padel racket\s*/i, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
    },

    baseModelName(title) {
      let t = this.norm(this.shortName(title)).replace(/\b(19|20)\d{2}\b/g, ' ');
      EP_COLOUR_WORDS.forEach(function (c) {
        t = t.replace(new RegExp('\\b' + c + '\\b', 'g'), ' ');
      });
      return t.replace(/\s{2,}/g, ' ').trim();
    },

    parseShape(value) {
      const v = this.norm(value);
      if (v.indexOf('round') > -1) return 'round';
      if (v.indexOf('teardrop') > -1 || v.indexOf('tear drop') > -1) return 'teardrop';
      if (v.indexOf('hybrid') > -1) return 'hybrid';
      if (v.indexOf('diamond') > -1) return 'diamond';
      return null;
    },

    parseBalance(value) {
      const v = this.norm(value);
      if (v.indexOf('low') > -1) return 'low';
      if (v.indexOf('high') > -1) return 'high';
      if (v.indexOf('medium') > -1 || v.indexOf('even') > -1 || v.indexOf('mid') > -1) return 'medium';
      return null;
    },

    parseWeightMid(value) {
      const matches = String(value || '').match(/\d{3}/g);
      if (!matches || matches.length === 0) return null;
      let sum = 0;
      matches.forEach(function (m) { sum += parseInt(m, 10); });
      return Math.round(sum / matches.length);
    },

    parseMaterial(value) {
      const v = this.norm(value);
      if (!v) return null;
      const isGlass = v.indexOf('glass') > -1 || v.indexOf('fibre') > -1 || v.indexOf('fiber') > -1;
      const isCarbon = v.indexOf('carbon') > -1 || /\d+\s*k/.test(v);
      const kMatch = v.match(/(\d+)\s*k/);
      return {
        family: isGlass && !isCarbon ? 'fiberglass' : (isCarbon ? 'carbon' : 'other'),
        k: kMatch ? parseInt(kMatch[1], 10) : null,
        raw: String(value).trim()
      };
    },

    parseSurface(value) {
      const v = this.norm(value);
      if (v.indexOf('rough') > -1 || v.indexOf('textur') > -1 || v.indexOf('sand') > -1) return 'rough';
      if (v.indexOf('smooth') > -1) return 'smooth';
      return null;
    },

    profile(item) {
      const p = {
        name: this.shortName(item.title),
        baseModel: this.baseModelName(item.title),
        shape: this.parseShape(item.shape),
        balance: this.parseBalance(item.balance),
        weight: this.parseWeightMid(item.weight),
        material: this.parseMaterial(item.material),
        surface: this.parseSurface(item.surface),
        level: (item.level || '').trim()
      };
      p.score = this.powerScore(p);
      p.identity = this.identity(p);
      return p;
    },

    powerScore(p) {
      if (!p.shape) return null;
      let score = EP_COPY.shapeTraits[p.shape].base;
      if (p.balance === 'low') score -= 0.4;
      if (p.balance === 'high') score += 0.4;
      if (p.weight !== null) {
        if (p.weight >= 370) score += 0.2;
        if (p.weight <= 350) score -= 0.2;
      }
      return score;
    },

    /* Identity is anchored on shape. A round racket stays a control racket even
       when it scores slightly higher than another control racket. */
    identity(p) {
      if (p.score === null) return null;
      if (p.shape === 'round') return 'control';
      if (p.shape === 'diamond') return 'power';
      if (p.score <= 1.4) return 'control';
      if (p.score <= 2.4) return 'allround';
      return 'power';
    },

    magnitude(a, b) {
      if (a.score === null || b.score === null) return 'unknown';
      const delta = Math.abs(a.score - b.score);
      if (delta < 0.15) return 'none';
      if (delta < 0.7) return 'slight';
      if (delta < 1.5) return 'noticeable';
      return 'major';
    },

    parsePrice(value) {
      const m = String(value || '').replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
      return m ? parseFloat(m[1]) : null;
    },

    priceGap(priceA, priceB) {
      const p1 = this.parsePrice(priceA);
      const p2 = this.parsePrice(priceB);
      if (!p1 || !p2) return 'unknown';
      const ratio = Math.max(p1, p2) / Math.min(p1, p2);
      if (ratio >= 1.6) return 'large';
      if (ratio >= 1.25) return 'moderate';
      return 'none';
    },

    levelRank(level) {
      const v = this.norm(level);
      if (!v) return null;
      if (v.indexOf('junior') > -1) return 1;
      if (v.indexOf('beginner') > -1 && v.indexOf('intermediate') > -1) return 3;
      if (v.indexOf('beginner') > -1) return 2;
      if (v.indexOf('intermediate') > -1 && v.indexOf('advanced') > -1) return 5;
      if (v.indexOf('intermediate') > -1) return 4;
      if (v.indexOf('advanced') > -1) return 6;
      if (v.indexOf('pro') > -1) return 7;
      return null;
    },

    stepUpRacket(a, b, itemA, itemB) {
      const ra = this.levelRank(a.level);
      const rb = this.levelRank(b.level);
      if (ra !== null && rb !== null && ra !== rb) return ra > rb ? a : b;
      const pa = this.parsePrice(itemA.price);
      const pb = this.parsePrice(itemB.price);
      if (pa && pb && pa !== pb) return pa > pb ? a : b;
      return null;
    },

    specsIdentical(a, b) {
      const shapeSame = a.shape && b.shape && a.shape === b.shape;
      const balanceSame = (!a.balance && !b.balance) || (a.balance && b.balance && a.balance === b.balance);
      const surfaceSame = (!a.surface && !b.surface) || (a.surface && b.surface && a.surface === b.surface);
      const matSame = (!a.material && !b.material) || (a.material && b.material && a.material.family === b.material.family && a.material.k === b.material.k);
      const weightSame = (a.weight === null || b.weight === null) || Math.abs(a.weight - b.weight) < 5;
      const levelSame = this.norm(a.level) === this.norm(b.level);
      return Boolean(shapeSame && balanceSame && surfaceSame && matSame && weightSame && levelSame);
    },

    pick(variants, seed) {
      return variants[Math.abs(seed) % variants.length];
    },

    build(itemA, itemB) {
      const a = this.profile(itemA);
      const b = this.profile(itemB);
      const seed = (parseInt(itemA.id, 10) || 0) + (parseInt(itemB.id, 10) || 0);

      const lines = [];
      const same = [];

      const colourway = a.baseModel !== '' && a.baseModel === b.baseModel;
      const identical = this.specsIdentical(a, b);
      const magnitude = colourway || identical ? 'none' : this.magnitude(a, b);
      const priceGap = colourway ? 'none' : this.priceGap(itemA.price, itemB.price);
      const levelsDiffer = Boolean(a.level && b.level && this.norm(a.level) !== this.norm(b.level));
      const stepUp = this.stepUpRacket(a, b, itemA, itemB);

      /* Same racket in different colours, or identical on paper */
      if (colourway) {
        lines.push({
          label: null,
          text: 'These are the same racket in different colours. There is no performance difference between them.'
        });
        return {
          lines: lines,
          same: this.buildSameList(a, b),
          short: this.pick([
            'Nothing separates these on court. Choose the design you like best.',
            'Identical rackets, different looks. Pick on colour.'
          ], seed),
          context: { identity_a: a.identity, identity_b: b.identity, magnitude: 'none', colourway: true, identical: true, price_gap: 'none', levels_differ: false },
          wantAi: false
        };
      }

      if (identical) {
        lines.push({
          label: null,
          text: 'On paper these are identical: same shape, balance, weight, face and surface. Any difference comes down to feel and design.'
        });
      }

      /* Shape */
      if (a.shape && b.shape && a.shape !== b.shape) {
        const upper = EP_COPY.shapeTraits[a.shape].base > EP_COPY.shapeTraits[b.shape].base ? a : b;
        const lower = upper === a ? b : a;
        lines.push({
          label: 'Shape',
          text: 'The ' + a.name + ' is ' + a.shape + ' shaped, the ' + b.name + ' ' + b.shape + '. The ' + upper.name + ' gives you ' + EP_COPY.shapeTraits[upper.shape].gives + ', while the ' + lower.name + ' gives you ' + EP_COPY.shapeTraits[lower.shape].gives + '.'
        });
      } else if (a.shape && b.shape && a.shape === b.shape) {
        same.push('a ' + a.shape + ' shape');
      }

      /* Balance, tempered by how far apart they are */
      if (a.balance && b.balance && a.balance !== b.balance) {
        const order = { low: 1, medium: 2, high: 3 };
        const higher = order[a.balance] > order[b.balance] ? a : b;
        const lowerB = higher === a ? b : a;
        const gap = Math.abs(order[a.balance] - order[b.balance]);
        if (gap === 2) {
          lines.push({
            label: 'Balance',
            text: 'The ' + higher.name + ' carries its weight high toward the head, adding real punch to smashes. The ' + lowerB.name + ' holds it low, so it feels much quicker in the hand and easier at the net.'
          });
        } else {
          lines.push({
            label: 'Balance',
            text: 'The ' + higher.name + ' carries slightly more weight toward the head, giving a touch more punch on smashes; the ' + lowerB.name + ' feels a fraction quicker in the hand. Not a big difference in play.'
          });
        }
      } else if (a.balance && b.balance && a.balance === b.balance) {
        same.push(a.balance + ' balance');
      }

      /* Weight, ignored under 8g, tempered under 15g */
      if (a.weight !== null && b.weight !== null) {
        const diff = Math.abs(a.weight - b.weight);
        if (diff >= 15) {
          const lighter = a.weight < b.weight ? a : b;
          const heavier = lighter === a ? b : a;
          lines.push({
            label: 'Weight',
            text: 'The ' + lighter.name + ' is around ' + diff + 'g lighter, so it swings faster and is easier on the arm. The ' + heavier.name + ' puts more weight behind the ball for extra stability and power.'
          });
        } else if (diff >= 8) {
          const lighter = a.weight < b.weight ? a : b;
          lines.push({
            label: 'Weight',
            text: 'The ' + lighter.name + ' is slightly lighter, so it will feel a touch quicker to swing, though the difference is small.'
          });
        }
      }

      /* Face material */
      if (a.material && b.material) {
        if (a.material.family !== b.material.family && a.material.family !== 'other' && b.material.family !== 'other') {
          const carbon = a.material.family === 'carbon' ? a : b;
          const glass = carbon === a ? b : a;
          lines.push({
            label: 'Face',
            text: 'The ' + carbon.name + ' has a carbon face that feels firmer and more direct. The ' + glass.name + ' uses fiberglass, which is softer and more comfortable on impact.'
          });
        } else if (a.material.family === 'carbon' && b.material.family === 'carbon' && a.material.k && b.material.k && a.material.k !== b.material.k) {
          const firmer = a.material.k < b.material.k ? a : b;
          const softer = firmer === a ? b : a;
          lines.push({
            label: 'Face',
            text: 'Both have carbon faces. The ' + firmer.name + '\'s ' + firmer.material.k + 'K weave is a touch firmer and more direct than the ' + softer.name + '\'s ' + softer.material.k + 'K, though the difference is small and most players won\'t notice it.'
          });
        } else if (a.material.family === b.material.family && a.material.family !== 'other') {
          same.push('a ' + (a.material.family === 'carbon' ? 'carbon' : 'fiberglass') + ' face');
        }
      }

      /* Surface */
      if (a.surface && b.surface) {
        if (a.surface !== b.surface) {
          const rough = a.surface === 'rough' ? a : b;
          lines.push({
            label: 'Surface',
            text: 'The ' + rough.name + '\'s rough surface grips the ball, making topspin and slice easier to add.'
          });
        } else if (a.surface === 'rough') {
          same.push('a rough, spin-friendly surface');
        }
      }

      /* Level */
      if (a.level && b.level && this.norm(a.level) !== this.norm(b.level)) {
        lines.push({
          label: 'Level',
          text: 'The ' + a.name + ' suits ' + a.level.toLowerCase() + ' players, the ' + b.name + ' ' + b.level.toLowerCase() + '.'
        });
      }

      return {
        lines: lines,
        same: this.buildSameList(a, b),
        short: this.buildShort(a, b, magnitude, identical, seed, { priceGap: priceGap, levelsDiffer: levelsDiffer, stepUp: stepUp }),
        context: { identity_a: a.identity, identity_b: b.identity, magnitude: magnitude, colourway: false, identical: identical, price_gap: priceGap, levels_differ: levelsDiffer },
        wantAi: true
      };
    },

    buildSameList(a, b) {
      const same = [];
      if (a.shape && a.shape === b.shape) same.push('a ' + a.shape + ' shape');
      if (a.balance && a.balance === b.balance) same.push(a.balance + ' balance');
      if (a.material && b.material && a.material.family === b.material.family && a.material.family !== 'other') {
        same.push('a ' + (a.material.family === 'carbon' ? 'carbon' : 'fiberglass') + ' face');
      }
      if (a.surface && a.surface === b.surface && a.surface === 'rough') same.push('a rough, spin-friendly surface');
      return same;
    },

    buildShort(a, b, magnitude, identical, seed, opts) {
      const labels = EP_COPY.identityLabels;
      opts = opts || {};

      /* A matching spec sheet does not mean matching rackets when they sit at
         different levels or price tiers. Construction and materials differ; the
         worker explains what the step up buys using the descriptions. */
      if ((identical || magnitude === 'none') && (opts.levelsDiffer || opts.priceGap === 'large')) {
        const step = opts.stepUp || (a.score >= b.score ? a : b);
        return this.pick([
          'Similar specs on paper, but these sit in different tiers. The ' + step.name + ' is the step up in build and intended level.',
          'On paper they look close, yet they are pitched at different levels. The ' + step.name + ' is the higher-spec option.'
        ], seed);
      }

      if (identical || magnitude === 'none') {
        return this.pick([
          'There is nothing meaningful between these two on paper. Choose on feel and design.',
          'Performance-wise these are near enough the same racket. Pick the one you prefer the look of.'
        ], seed);
      }

      if (a.identity && b.identity && a.identity === b.identity) {
        const punchier = a.score > b.score ? a : b;
        const other = punchier === a ? b : a;
        if (magnitude === 'slight') {
          return this.pick([
            'Both are ' + labels[a.identity] + ' rackets. The ' + punchier.name + ' offers a touch more punch, but there is not much in it.',
            'These are very similar ' + labels[a.identity] + ' rackets. The ' + punchier.name + ' hits slightly harder, the ' + other.name + ' is a fraction easier to handle.'
          ], seed);
        }
        return this.pick([
          'Both lean toward ' + labels[a.identity] + ', but the ' + punchier.name + ' is clearly the harder-hitting of the two.',
          'Two ' + labels[a.identity] + ' rackets, with the ' + punchier.name + ' noticeably the more aggressive option.'
        ], seed);
      }

      if (a.identity && b.identity) {
        const pair = [a, b].sort(function (x, y) { return x.score - y.score; });
        const softer = pair[0];
        const harder = pair[1];
        if (softer.identity === 'control' && harder.identity === 'power') {
          return this.pick([
            'The ' + harder.name + ' is built for power and attacking play. The ' + softer.name + ' is all about control and forgiveness.',
            'These sit at opposite ends: the ' + harder.name + ' for aggression, the ' + softer.name + ' for control and easy handling.'
          ], seed);
        }
        if (softer.identity === 'control' && harder.identity === 'allround') {
          return this.pick([
            'The ' + harder.name + ' is the more rounded racket. The ' + softer.name + ' keeps things focused on control and easy handling.',
            'The ' + softer.name + ' is the pure control option; the ' + harder.name + ' gives you a bit more of everything.'
          ], seed);
        }
        return this.pick([
          'The ' + harder.name + ' is the more aggressive, power-focused racket. The ' + softer.name + ' covers more bases.',
          'The ' + softer.name + ' is the more versatile of the two; the ' + harder.name + ' is tuned for attacking play.'
        ], seed);
      }

      return '';
    }
  };
  /* ── EP END ── */

  const loadCompareItems = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) ?? [];
    } catch {
      return [];
    }
  };

  const saveCompareItems = (items) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
    FoxTheme.pubsub.publish(COMPARE_CHANGE_EVENT, items);
  };

  const removeCompareItem = (id) => {
    saveCompareItems(loadCompareItems().filter((item) => String(item.id) !== id));
  };

  const compareIds = (items) => new Set(items.map((item) => String(item.id)));

  const applyButtonState = (button, ids) => {
    const active = ids.has(button.dataset.compareId);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
  };

  class CompareToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-compare-id]');
      if (!this.button) return;

      this.onClick = this.handleClick.bind(this);
      this.button.addEventListener('click', this.onClick);

      this.onChange = (items = loadCompareItems()) => applyButtonState(this.button, compareIds(items));
      this.unsubscribe = FoxTheme.pubsub.subscribe(COMPARE_CHANGE_EVENT, this.onChange);

      this.onChange();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      this.unsubscribe?.();
    }

    handleClick(event) {
      event.preventDefault();

      const items = loadCompareItems();
      const index = items.findIndex((item) => String(item.id) === this.button.dataset.compareId);

      if (index > -1) {
        items.splice(index, 1);
      } else {
        if (items.length >= Number(this.dataset.maxItems)) {
          alert(this.dataset.textMaxAlert);
          return;
        }

        let item;
        try {
          item = JSON.parse(this.button.dataset.compareItem);
        } catch {
          return;
        }
        items.push(item);
      }

      saveCompareItems(items);
    }
  }

  customElements.define('compare-toggle', CompareToggle);

  class ProductCompare extends HTMLElement {
    connectedCallback() {
      this.tray = this.querySelector('.compare-tray');
      this.trayItems = this.querySelector('.compare-tray__items');

      this.trayItems.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-compare-remove]');
        if (removeButton) removeCompareItem(removeButton.dataset.compareRemove);
      });

      this.querySelector('.compare-tray__clear').addEventListener('click', () => saveCompareItems([]));

      this.onChange = (items) => this.sync(items);
      this.unsubscribe = FoxTheme.pubsub.subscribe(COMPARE_CHANGE_EVENT, this.onChange);

      this.sync();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    sync(items = loadCompareItems()) {
      this.tray.hidden = items.length === 0;
      this.trayItems.replaceChildren(...items.map((item) => this.buildTrayItem(item)));
    }

    buildTrayItem(item) {
      const thumb = document.createElement('li');
      thumb.className = 'compare-tray__thumb';

      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.title ?? '';
        image.width = 44;
        image.height = 44;
        thumb.append(image);
      }

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'compare-tray__remove';
      removeButton.dataset.compareRemove = item.id;
      removeButton.setAttribute('aria-label', `${this.dataset.textRemove}: ${item.title ?? ''}`);
      removeButton.textContent = '\u00d7';
      thumb.append(removeButton);

      return thumb;
    }
  }

  customElements.define('product-compare', ProductCompare);

  class ProductCompareModal extends ModalComponent {
    get isLockingNeeded() {
      return true;
    }

    connectedCallback() {
      super.connectedCallback();
      this.texts = this.closest('product-compare').dataset;
      this.errorMessage = this.querySelector('.compare-modal__error');
      /* ── EP START: verdict elements ── */
      this.verdictPanel = this.querySelector('[data-compare-verdict]');
      this.verdictLines = this.querySelector('[data-verdict-lines]');
      this.verdictLoading = this.querySelector('[data-verdict-loading]');
      this.verdictShort = this.querySelector('[data-verdict-short]');
      this.verdictRequestToken = 0;
      /* ── EP END ── */
      this.addEventListener('click', this.handleClick.bind(this));
    }

    handleClick(event) {
      const addButton = event.target.closest('[data-compare-add]');
      if (addButton) this.addToCart(addButton);
    }

    prepareToShow() {
      this.errorMessage.hidden = true;
      const items = loadCompareItems();
      this.buildTable(items);
      /* ── EP START: build verdict ── */
      this.buildVerdict(items);
      /* ── EP END ── */
    }

    /* ── EP START: verdict rendering ── */
    buildVerdict(items) {
      if (!this.verdictPanel) return;

      this.verdictRequestToken += 1;
      const token = this.verdictRequestToken;

      this.verdictLines.replaceChildren();
      this.verdictShort.hidden = true;
      this.verdictShort.textContent = '';
      this.stopLoadingCycle();
      this.verdictLoading.hidden = true;

      if (items.length !== 2) {
        this.verdictPanel.hidden = true;
        return;
      }

      const result = EPCompareEngine.build(items[0], items[1]);

      /* Show only the most decision-relevant lines. The spec table underneath is
         the full evidence for anyone who wants to dig. */
      const priority = { Shape: 1, Level: 2, Face: 3, Balance: 4, Weight: 5, Surface: 6 };
      const sorted = result.lines.slice().sort(function (x, y) {
        return (x.label ? priority[x.label] || 9 : 0) - (y.label ? priority[y.label] || 9 : 0);
      });
      const displayLines = sorted.slice(0, 4);
      this.epSameLineText = result.same.length > 0 ? EP_COPY.bothPrefix + result.same.join(', ') + '.' : null;

      if (result.short) {
        this.verdictShort.textContent = result.short;
        this.verdictShort.hidden = false;
      }

      this.verdictPanel.hidden = false;

      const meaningfulLines = result.lines.filter((line) => line.label !== null);
      const needsAi = result.wantAi
        && (meaningfulLines.length < 2 || result.context.price_gap === 'large')
        && Boolean(items[0].handle && items[1].handle);

      /* When the deeper read is coming, hold the spec lines back so the panel
         fills in once instead of visibly swapping content under the reader.
         The held lines are the fallback if the deeper read fails. */
      if (needsAi) {
        this.epFallbackLines = displayLines;
        this.fetchAiVerdict(items, result.context, token);
      } else {
        this.epFallbackLines = null;
        displayLines.forEach((line) => this.appendVerdictLine(line));
        if (this.epSameLineText) {
          this.appendVerdictLine({ label: null, text: this.epSameLineText });
        }
      }
    }

    renderVerdictFallback() {
      if (this.verdictLines.childElementCount > 0) return;
      (this.epFallbackLines || []).forEach((line) => this.appendVerdictLine(line));
      if (this.epSameLineText) {
        this.appendVerdictLine({ label: null, text: this.epSameLineText });
      }
    }

    appendVerdictLine(line) {
      const li = document.createElement('li');
      if (line.label) {
        const label = document.createElement('span');
        label.className = 'ep-verdict-label';
        label.textContent = line.label + ': ';
        li.append(label);
      }
      li.append(line.text);
      this.verdictLines.append(li);
    }

    startLoadingCycle() {
      this.stopLoadingCycle();
      const label = this.verdictLoading.querySelector('[data-verdict-loading-text]');
      if (!label) return;
      let index = 0;
      label.textContent = EP_COPY.loadingMessages[0];
      this.epLoadingTimer = setInterval(() => {
        index = (index + 1) % EP_COPY.loadingMessages.length;
        label.textContent = EP_COPY.loadingMessages[index];
      }, 1700);
    }

    stopLoadingCycle() {
      if (this.epLoadingTimer) {
        clearInterval(this.epLoadingTimer);
        this.epLoadingTimer = null;
      }
    }

    async fetchAiVerdict(items, context, token) {
      this.startLoadingCycle();
      this.verdictLoading.hidden = false;

      const payload = {
        action: 'compare',
        context: context,
        products: items.map((item) => ({
          id: item.id,
          handle: item.handle,
          title: item.title,
          specs: {
            shape: item.shape,
            balance: item.balance,
            weight: item.weight,
            surface: item.surface,
            material: item.material,
            level: item.level,
            price: item.price
          }
        }))
      };

      try {
        const response = await fetch(EP_COMPARE_WORKER_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await response.json();

        if (token !== this.verdictRequestToken) return;
        this.stopLoadingCycle();
        this.verdictLoading.hidden = true;

        const aiLines = data.success && data.verdict
          ? (data.verdict.differences || []).filter((line) => line && line.text)
          : [];

        if (aiLines.length > 0) {
          aiLines.forEach((line) => this.appendVerdictLine({ label: line.label || null, text: line.text }));
          if (this.epSameLineText) {
            this.appendVerdictLine({ label: null, text: this.epSameLineText });
          }
          if (data.verdict.in_short) {
            this.verdictShort.textContent = data.verdict.in_short;
            this.verdictShort.hidden = false;
          }
        } else {
          this.renderVerdictFallback();
        }
      } catch {
        if (token === this.verdictRequestToken) {
          this.stopLoadingCycle();
          this.verdictLoading.hidden = true;
          this.renderVerdictFallback();
        }
      }
    }
    /* ── EP END ── */

    buildTable(items) {
      this.querySelectorAll('[data-compare-row]').forEach((row) => {
        row.querySelectorAll('td').forEach((cell) => cell.remove());
        items.forEach((item) => row.append(this.buildCell(row.dataset.compareRow, item)));
      });
    }

    buildCell(key, item) {
      const cell = document.createElement('td');

      switch (key) {
        case 'header': {
          if (item.image) {
            const image = document.createElement('img');
            image.src = item.image;
            image.alt = '';
            cell.append(image);
          }
          const name = document.createElement('div');
          name.className = 'compare-modal__name med-14px';
          name.textContent = item.title ?? '';
          cell.append(name);
          break;
        }
        case 'price': {
          if (item.compareAtPrice) {
            const compareAt = document.createElement('s');
            compareAt.textContent = item.compareAtPrice;
            cell.append(compareAt, ' ');
          }
          cell.append(item.price ?? '');
          break;
        }
        case 'stock':
          cell.textContent = item.available ? this.texts.textInStock : this.texts.textOutOfStock;
          break;
        case 'actions': {
          const addButton = document.createElement('button');
          addButton.type = 'button';
          addButton.className = 'compare-modal__atc med-13px';
          addButton.textContent = this.texts.textAddToBasket;
          if (item.available) {
            addButton.dataset.compareAdd = item.variantId;
            if (item.youSave) addButton.dataset.youSave = item.youSave;
          } else {
            addButton.disabled = true;
          }
          cell.append(addButton);
          break;
        }
        default:
          cell.textContent = item[key] || '\u2014';
      }

      return cell;
    }

    async addToCart(button) {
      if (button.getAttribute('aria-disabled') === 'true') return;
      button.setAttribute('aria-disabled', 'true');
      this.errorMessage.hidden = true;

      const config = FoxTheme.utils.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const body = new FormData();
      body.append('id', button.dataset.compareAdd);
      body.append('quantity', 1);
      if (button.dataset.youSave) body.append('properties[You save]', button.dataset.youSave);
      config.body = body;

      try {
        const response = await fetch(FoxTheme.routes.cart_add_url, config);
        const parsed = await response.json();

        if (parsed.status) {
          const message = typeof parsed.description === 'string' ? parsed.description : parsed.message;
          this.showError(message || this.texts.textAddError);
          return;
        }

        this.hide();

        if (FoxTheme.settings.cartType === 'page') {
          window.location.href = FoxTheme.routes.cart_url;
          return;
        }

        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsed } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
      } catch {
        this.showError(this.texts.textAddError);
      } finally {
        button.removeAttribute('aria-disabled');
      }
    }

    showError(message) {
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
    }
  }

  customElements.define('product-compare-modal', ProductCompareModal);
}